import cornerSquareTypes from "../../../constants/cornerSquareTypes";
import { CornerSquareType, DrawArgs, BasicFigureDrawArgs, RotateFigureArgs } from "../../../types";

/**
 * Worker-safe QR corner square renderer — produces SVG markup strings instead of DOM elements.
 */
export default class QRCornerSquare {
  _svgString: string = "";
  _vectorPath: string = "";
  _type: CornerSquareType;

  constructor({ type }: { svg?: unknown; type: CornerSquareType }) {
    this._type = type;
  }

  draw(x: number, y: number, size: number, rotation: number): void {
    const type = this._type;
    let drawFunction;

    switch (type) {
      case cornerSquareTypes.square:
        drawFunction = this._drawSquare;
        break;
      case cornerSquareTypes.extraRounded:
        drawFunction = this._drawExtraRounded;
        break;
      case cornerSquareTypes.dot:
      default:
        drawFunction = this._drawDot;
    }

    drawFunction.call(this, { x, y, size, rotation });
  }

  _rotateFigure({ x, y, size, rotation = 0, draw }: RotateFigureArgs): void {
    const cx = x + size / 2;
    const cy = y + size / 2;

    draw();
    if (rotation !== 0 && this._svgString) {
      const transformAttr = ` transform="rotate(${(180 * rotation) / Math.PI},${cx},${cy})"`;
      if (this._svgString.endsWith("/>")) {
        this._svgString = this._svgString.slice(0, -2) + transformAttr + "/>";
      } else {
        const firstClose = this._svgString.indexOf(">");
        if (firstClose > 0) {
          this._svgString =
            this._svgString.slice(0, firstClose) + transformAttr + this._svgString.slice(firstClose);
        }
      }
    }
  }

  _basicDot(args: BasicFigureDrawArgs): void {
    const { size, x, y } = args;
    const dotSize = size / 7;

    this._rotateFigure({
      ...args,
      draw: () => {
        this._svgString = `<path clip-rule="evenodd" d="${
          `M ${x + size / 2} ${y}` +
          `a ${size / 2} ${size / 2} 0 1 0 0.1 0` +
          `z` +
          `m 0 ${dotSize}` +
          `a ${size / 2 - dotSize} ${size / 2 - dotSize} 0 1 1 -0.1 0` +
          `Z`
        }"/>`;
      }
    });
  }

  _basicSquare(args: BasicFigureDrawArgs): void {
    const { size, x, y } = args;
    const dotSize = size / 7;

    this._rotateFigure({
      ...args,
      draw: () => {
        this._svgString = `<path clip-rule="evenodd" d="${
          `M ${x} ${y}` +
          `v ${size}` +
          `h ${size}` +
          `v ${-size}` +
          `z` +
          `M ${x + dotSize} ${y + dotSize}` +
          `h ${size - 2 * dotSize}` +
          `v ${size - 2 * dotSize}` +
          `h ${-size + 2 * dotSize}` +
          `z`
        }"/>`;
      }
    });
  }

  _basicExtraRounded(args: BasicFigureDrawArgs): void {
    const { size, x, y } = args;
    const dotSize = size / 7;
    const outerRadius = 2.5 * dotSize;
    const innerRadius = 1.5 * dotSize;
    const curveFactor = 4 * (Math.sqrt(2) - 1) / 3;
    const outerControl = outerRadius * curveFactor;
    const innerControl = innerRadius * curveFactor;
    const left = x;
    const right = x + size;
    const top = y;
    const bottom = y + size;
    this._vectorPath =
      `M ${left} ${top + outerRadius}` +
      `L ${left} ${bottom - outerRadius}` +
      `C ${left} ${bottom - outerRadius + outerControl} ${left + outerRadius - outerControl} ${bottom} ${left + outerRadius} ${bottom}` +
      `L ${right - outerRadius} ${bottom}` +
      `C ${right - outerRadius + outerControl} ${bottom} ${right} ${bottom - outerRadius + outerControl} ${right} ${bottom - outerRadius}` +
      `L ${right} ${top + outerRadius}` +
      `C ${right} ${top + outerRadius - outerControl} ${right - outerRadius + outerControl} ${top} ${right - outerRadius} ${top}` +
      `L ${left + outerRadius} ${top}` +
      `C ${left + outerRadius - outerControl} ${top} ${left} ${top + outerRadius - outerControl} ${left} ${top + outerRadius} Z` +
      `M ${left + outerRadius} ${top + dotSize}` +
      `L ${right - outerRadius} ${top + dotSize}` +
      `C ${right - outerRadius + innerControl} ${top + dotSize} ${right - dotSize} ${top + outerRadius - innerControl} ${right - dotSize} ${top + outerRadius}` +
      `L ${right - dotSize} ${bottom - outerRadius}` +
      `C ${right - dotSize} ${bottom - outerRadius + innerControl} ${right - outerRadius + innerControl} ${bottom - dotSize} ${right - outerRadius} ${bottom - dotSize}` +
      `L ${left + outerRadius} ${bottom - dotSize}` +
      `C ${left + outerRadius - innerControl} ${bottom - dotSize} ${left + dotSize} ${bottom - outerRadius + innerControl} ${left + dotSize} ${bottom - outerRadius}` +
      `L ${left + dotSize} ${top + outerRadius}` +
      `C ${left + dotSize} ${top + outerRadius - innerControl} ${left + outerRadius - innerControl} ${top + dotSize} ${left + outerRadius} ${top + dotSize} Z`;

    this._rotateFigure({
      ...args,
      draw: () => {
        this._svgString = `<path clip-rule="evenodd" d="${this._vectorPath}"/>`;
      }
    });
  }

  _drawDot({ x, y, size, rotation }: DrawArgs): void {
    this._basicDot({ x, y, size, rotation });
  }

  _drawSquare({ x, y, size, rotation }: DrawArgs): void {
    this._basicSquare({ x, y, size, rotation });
  }

  _drawExtraRounded({ x, y, size, rotation }: DrawArgs): void {
    this._basicExtraRounded({ x, y, size, rotation });
  }
}
